import React, { useState, useContext } from "react";
import { useFormik } from "formik";
import YupObject from "yup/lib/object";
import YupString from "yup/lib/string";
import axios from "axios";
import { capitalize } from "utils/formatters";

import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormHelperText from "@mui/material/FormHelperText";
import DialogTitleWithClose from "./DialogTitleWithClose";
import SuccessFailedModal from "components/SuccessFailedModal";
import { Button } from "components/Buttons";
import ClinicAutocomplete from "./ClinicAutocomplete";

import { inputStyle } from "../styles";
import { ClinicRepository } from "repositories/ClinicRepository";
import { USER_GROUPS } from "constants/roles";
import { AuthContext } from "contexts/authContext";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { Option } from "types/common";

const validationSchema = new YupObject().shape({
  firstName: new YupString()
    .trim()
    .matches(/^[A-Za-z ]*$/, {
      //original matcher
      message: "Invalid format.",
    })
    .required("Clinic Admin first name is required"),
  lastName: new YupString()
    .trim()
    .matches(/^[A-Za-z ]*$/, {
      //original matcher
      message: "Invalid format.",
    })
    .required("Clinic Admin last name is required"),
  email: new YupString()
    .trim()
    .email("Enter valid email")
    .required("Clinic Admin email is required"),
  clinic: new YupString().trim().required("Select Clinic"),
});

const AddClinicAdminModalForm: React.FC<{
  isOpen: boolean;
  closeModal: () => void;
  clinicId?: string;
  clinicOptions?: Option[];
  getList?: () => void;
  onClinicChange: (value: string) => void;
}> = (props) => {
  const authContext = useContext(AuthContext);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      clinic: props.clinicId || "",
    },
    validationSchema,
    onSubmit: async (data) => {
      try {
        const email = data.email.toLowerCase();
        const clinicianRepository = ClinicRepository.getInstance();
        await clinicianRepository.createClinicianOnInvite({
          email,
          firstName: capitalize(data.firstName),
          lastName: capitalize(data.lastName),
          clinicId: data.clinic,
          role: [USER_GROUPS.CLINIC_ADMIN],
        });

        await authContext.signUpAdmin(email);

        if (authContext.adminAddUserToGroup) {
          await authContext.adminAddUserToGroup(
            email,
            USER_GROUPS.CLINIC_ADMIN
          );
        }

        setIsSuccessModalOpen(true);
        formik.resetForm();
        if (props.getList) {
          props.getList();
        }
      } catch (err) {
        if (axios.isAxiosError(err)) {
          formik.setFieldError("email", err.response?.data);
        }
      }
    },
  });

  const clearData = () => {
    formik.resetForm();
    props.closeModal();
  };

  const handleChange = (option: Option | null) => {
    formik.setFieldValue("clinic", option?.value);
  };

  React.useEffect(() => {
    if (props.clinicId) {
      formik.setFieldValue("clinic", props.clinicId);
    }
  }, [props.clinicId]);

  const isButtonDisabled = React.useMemo(() => {
    return Boolean(
      formik.errors.email ||
        formik.errors.firstName ||
        formik.errors.lastName ||
        formik.errors.clinic
    );
  }, [
    formik.errors.email,
    formik.errors.firstName,
    formik.errors.lastName,
    formik.errors.clinic,
  ]);

  return (
    <>
      <Dialog
        open={props.isOpen}
        onClose={clearData}
        scroll="body"
        PaperProps={{
          sx: {
            width: "382px",
            overflowY: "unset",
          },
        }}
      >
        <DialogTitleWithClose
          onModalClose={clearData}
          title="Add Clinic Admin"
        />

        <DialogContent sx={{ mt: "20px" }}>
          <Grid container>
            <TextField
              variant="standard"
              placeholder="Enter First Name"
              value={capitalize(formik.values.firstName)}
              fullWidth
              margin="normal"
              style={inputStyle}
              InputProps={{
                disableUnderline: true,
                style: {
                  color: "#5C5D68",
                },
              }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="firstName"
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <FormHelperText
                error={Boolean(
                  formik.touched.firstName && formik.errors.firstName
                )}
                sx={{ paddingLeft: 1 }}
              >
                {formik.errors.firstName}
              </FormHelperText>
            )}

            <TextField
              variant="standard"
              placeholder="Enter Last Name"
              value={capitalize(formik.values.lastName)}
              fullWidth
              margin="normal"
              style={inputStyle}
              InputProps={{
                disableUnderline: true,
                style: {
                  color: "#5C5D68",
                },
              }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="lastName"
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <FormHelperText
                error={Boolean(
                  formik.touched.lastName && formik.errors.lastName
                )}
                sx={{ paddingLeft: 1 }}
              >
                {formik.errors.lastName}
              </FormHelperText>
            )}

            <TextField
              variant="standard"
              placeholder="Email"
              value={formik.values.email}
              fullWidth
              margin="normal"
              style={inputStyle}
              InputProps={{
                disableUnderline: true,
                style: {
                  color: "#5C5D68",
                },
              }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="email"
            />
            {formik.touched.email && formik.errors.email && (
              <FormHelperText
                error={Boolean(formik.touched.email && formik.errors.email)}
                sx={{ paddingLeft: 1 }}
              >
                {formik.errors.email}
              </FormHelperText>
            )}
            {!props.clinicId && props.clinicOptions && (
              <ClinicAutocomplete
                options={props.clinicOptions}
                onChange={props.onClinicChange}
                handleChange={handleChange}
                placeholder="Select a Clinic"
                onBlur={formik.handleBlur}
              />
            )}
            {formik.touched.clinic && formik.errors.clinic && (
              <FormHelperText
                error={Boolean(formik.touched.clinic && formik.errors.clinic)}
                sx={{ paddingLeft: 1 }}
              >
                {formik.errors.clinic}
              </FormHelperText>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ padding: "0px 24px 24px" }}>
          <Grid container justifyContent="flex-end">
            <Button
              title="Send Invite"
              onClick={formik.handleSubmit}
              type={ButtonType.normal}
              colorScheme={
                isButtonDisabled
                  ? ButtonColorScheme.disabled
                  : ButtonColorScheme.gradient
              }
              disabled={isButtonDisabled}
              shadowed
            />
          </Grid>
        </DialogActions>
      </Dialog>

      <SuccessFailedModal
        isError={false}
        successMessage="Clinic Admin Added"
        open={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
      />
    </>
  );
};

export default AddClinicAdminModalForm;
